import React, { useMemo } from 'react';
import { AccountStack } from './account-stack';
import { APP_ROUTES } from './routes';
import { ApplicationType } from '@/contexts/dynamic-config';
import { AppStackParams } from './types';
import { AppTabsToastContextProvider } from '@/contexts/app-tabs-toast-context-provider';
import { CatalogStack } from './partners/catalog-stack';
import { ChatStack } from './partners/chat-stack';
import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import { DrawerHeaderTablet } from '@/components/drawer-navigation/drawer-header-tablet';
import { ExternalContentWrapper } from '@/components/external-content/external-content-wrapper';
import {
  getActiveRoute,
  NavigationState,
  NavigationStateRoute,
} from '@/utils/navigation';
import { LayoutSize, useLayoutContext } from '@/contexts/layout-context';
import { MenuInfoStack } from './menu-info-stack';
import { NavigationDrawerContextProvider } from '@/contexts/navigation-drawer-context';
import { NotificationBadgeCountContainer } from '@/components/notification-badge-count-container';
import { OrderGuideStack } from './partners/order-guide-stack';
import { OrdersStack } from './orders-stack';
import { partnerSupplier } from '@/utils/environment';
import { PaymentsStack } from './payments-stack';
import { Platform, StyleSheet } from 'react-native';
import { RestaurantStack } from './restaurant-stack';
import {
  StatePropagatingNavProps,
  StatePropagatingStack,
} from './state-propagating-tab-navigator';
import { StorefrontMicroProductTab } from '@/screens/storefront-microproducts/storefront-micro-product-tab';
import { TabBar } from '@/components/tab-bar';
import { TabBarIcon } from '@/components/tab-bar-icon';
import { TabBarLabel } from '@/components/tab-bar-label';
import { Theme, useTheme } from '@/contexts/theme-context';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import { useDynamicConfigValue } from '@/hooks/use-dynamic-config-value';
import { useHasRestaurantStack } from '@/hooks/use-has-restaurant-stack';
import { useKeyboardVisibility } from '@/hooks/use-keyboard-visibility';
import { useOrderInbounds } from '@/hooks/use-order-inbounds';

export const TabStack = createBottomTabNavigator<AppStackParams>();

interface Props {
  lastActiveTabRouteRef: React.RefObject<NavigationStateRoute | null>;
}

const APP_ROUTE_NAMES = new Set<string>(Object.values(APP_ROUTES));

export const AppTabs: React.FC<Props> = ({ lastActiveTabRouteRef }) => {
  const hasRestaurantStack = useHasRestaurantStack();
  const isKeyboardVisible = useKeyboardVisibility();
  const theme = useTheme();
  const { isSmallLayout, layoutSize } = useLayoutContext();
  const {
    selectedRestaurantRelationship: {
      paymentsOnlyEnabled,
      showMenuCosting,
      showPaymentsTab,
    },
  } = useAuthenticatedUserContext();
  const isCatalogLimited =
    useDynamicConfigValue('application_type') === ApplicationType.CATALOG_ONLY;
  const isTabletLayout =
    layoutSize === LayoutSize.MEDIUM || layoutSize === LayoutSize.SMALL_TABLET;
  const { isOrderAgentOnly } = useOrderInbounds();

  const content = useMemo(() => {
    let hideTabs = !isSmallLayout;

    // Android keyboard layout mode causes tabs to render above the keyboard
    // instead of behind, like on iOS. This hides the tabs if the keyboard
    // is showing on Android.
    // We cannot hide on iOS because the chat composer will not display above
    // the keyboard
    if (Platform.OS === 'android' && isKeyboardVisible) {
      hideTabs = true;
    }

    const navProps = getStackNavigatorProps(hideTabs, theme);

    // Routes whose entries live in the More popup (MORE_MENU_ABSORBED_ROUTES
    // hides their rail tiles). Registered in BOTH stack groups: the popup
    // builds its items from config/context data with no knowledge of which
    // group is mounted, so a group that omitted these would turn every
    // popup tap into a silently-dropped jumpTo (and eat /storefront boot
    // deep links) for that group's users — paymentsOnly included.
    const moreMenuAbsorbedGroup = !isSmallLayout ? (
      <>
        <StatePropagatingStack.Screen
          component={ExternalContentWrapper}
          name={APP_ROUTES.Links}
        />
        {/* In-container storefront microproducts: a hidden tab —
            no rail tile, reached only from the More popup. lazy:true (the
            Orders-tab escape hatch) defers the WebView mount until first
            visit; after that it intentionally stays mounted across tab
            switches so the SPA doesn't reload. Small layout pushes the
            stack screen instead (no tabs there — same split as
            Links/ExternalContentScreen). initialParams (not a live prop):
            the router consumes it exactly once at navigator mount — while
            lastActiveTabRouteRef still holds the pre-remount params — so a
            restaurant switch restores the open product, and later ref
            movement can never blank the mounted-but-blurred WebView. */}
        <StatePropagatingStack.Screen
          component={StorefrontMicroProductTab}
          initialParams={{
            productId:
              // @ts-ignore untyped route-params read, the Orders pattern
              lastActiveTabRouteRef?.current?.params?.productId,
          }}
          name={APP_ROUTES.StorefrontMicroProduct}
          options={{ lazy: true }}
        />
      </>
    ) : null;

    const paymentsOnlyStackGroup = (
      <>
        <StatePropagatingStack.Screen
          component={PaymentsStack}
          name={APP_ROUTES.Payments}
        />
        <StatePropagatingStack.Screen
          component={ChatStack}
          name={APP_ROUTES.Chat}
          options={{ lazy: true }}
        />
        {hasRestaurantStack ? (
          <>
            <StatePropagatingStack.Screen
              component={RestaurantStack}
              name={APP_ROUTES.Restaurant}
              options={{ lazy: true }}
            />
            <StatePropagatingStack.Screen
              component={AccountStack}
              name={APP_ROUTES.Account}
            />
          </>
        ) : null}
        {moreMenuAbsorbedGroup}
      </>
    );

    const defaultStackGroup = (
      <>
        {!isCatalogLimited ? (
          <StatePropagatingStack.Screen
            component={OrderGuideStack}
            name={APP_ROUTES.OrderGuide}
          />
        ) : null}
        <StatePropagatingStack.Screen
          component={CatalogStack}
          name={APP_ROUTES.Catalog}
          options={{ lazy: true }}
        />
        {!isCatalogLimited ? (
          <StatePropagatingStack.Screen
            children={() => (
              <OrdersStack
                initialCreditRequestUUID={undefined}
                initialInvoiceUUID={
                  // @ts-ignore
                  lastActiveTabRouteRef?.current?.params?.invoiceUUID
                }
                initialOrderUUID={
                  // @ts-ignore
                  lastActiveTabRouteRef?.current?.params?.orderUUID
                }
              />
            )}
            name={APP_ROUTES.Orders}
            options={{ lazy: true }}
          />
        ) : null}
        {!isOrderAgentOnly ? (
          <StatePropagatingStack.Screen
            component={ChatStack}
            name={APP_ROUTES.Chat}
            options={{ lazy: true }}
          />
        ) : null}
        {hasRestaurantStack ? (
          <>
            <StatePropagatingStack.Screen
              component={RestaurantStack}
              name={APP_ROUTES.Restaurant}
              options={{ lazy: true }}
            />
            <StatePropagatingStack.Screen
              component={AccountStack}
              name={APP_ROUTES.Account}
            />
          </>
        ) : null}
        {showPaymentsTab && !isCatalogLimited ? (
          <StatePropagatingStack.Screen
            component={PaymentsStack}
            name={APP_ROUTES.Payments}
            options={{ lazy: true }}
          />
        ) : null}
        {showMenuCosting && !isSmallLayout ? (
          <StatePropagatingStack.Screen
            component={MenuInfoStack}
            name={APP_ROUTES.MenuInfo}
          />
        ) : null}
        {moreMenuAbsorbedGroup}
      </>
    );

    const tabStack = (
      <AppTabsToastContextProvider>
        <StatePropagatingStack.Navigator
          initialRouteName={lastActiveTabRouteRef?.current?.name}
          screenListeners={{
            state: (e) => {
              // @ts-ignore
              const navigationState = e.data?.state as
                | NavigationState
                | undefined;
              if (navigationState) {
                /*
                 * Report active route name for APP_ROUTES only up to the parent.
                 * This will ensure selected tab is preserved even if user is on a
                 * screen nested under the tab (such as the search screen)
                 */
                const nextRoute = getActiveRoute(
                  navigationState,
                  APP_ROUTE_NAMES,
                );
                if (nextRoute) {
                  lastActiveTabRouteRef.current = nextRoute;
                }
              }
            },
          }}
          {...navProps}
        >
          <StatePropagatingStack.Group
            // Don't show the header on desktop because parent AppTabsContainerStack
            // renders header over top of the page.
            screenOptions={{ headerShown: isTabletLayout }}
          >
            {paymentsOnlyEnabled ? paymentsOnlyStackGroup : defaultStackGroup}
          </StatePropagatingStack.Group>
        </StatePropagatingStack.Navigator>
      </AppTabsToastContextProvider>
    );

    return isSmallLayout ? (
      tabStack
    ) : (
      <NavigationDrawerContextProvider>
        {tabStack}
      </NavigationDrawerContextProvider>
    );
  }, [
    isSmallLayout,
    isTabletLayout,
    isKeyboardVisible,
    theme,
    isCatalogLimited,
    hasRestaurantStack,
    showPaymentsTab,
    showMenuCosting,
    lastActiveTabRouteRef,
    paymentsOnlyEnabled,
    isOrderAgentOnly,
  ]);

  return (
    <NotificationBadgeCountContainer>{content}</NotificationBadgeCountContainer>
  );
};

const getStackNavigatorProps = (
  hideTabs: boolean,
  theme: Theme,
): Partial<StatePropagatingNavProps> => {
  return {
    // Lazy is set to false otherwise navigating to nested screens in another
    // tab doesn't work since the route isn't mounted
    screenOptions: ({ navigation, route: { name } }) => ({
      header: () => {
        const navigationState = navigation.getState();
        const isActiveRoute =
          navigationState.routeNames[navigationState.index] === name;
        return isActiveRoute ? <DrawerHeaderTablet /> : null;
      },
      lazy: false,
      tabBarIcon: (props) => <TabBarIcon {...props} routeName={name} />,
      tabBarLabel: (props) => <TabBarLabel {...props} routeName={name} />,
      tabBarStyle: {
        backgroundColor: theme.colors.transparent,
        borderTopColor: theme.colors.stroke,
        borderTopWidth: StyleSheet.hairlineWidth,
        display: hideTabs ? 'none' : undefined,
        elevation: 0,
        height: 60,
        paddingBottom: 6,
        paddingHorizontal: 20,
        paddingTop: 6,
        shadowOpacity: 0,
      },
      title: partnerSupplier.fullName,
    }),
    tabBar: (props) => <TabBar {...props} />,
  };
};
